import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { AccountsService } from './services/accounts.service';
import { AuthService } from './services/auth.service';
import { AnalyticsService } from './services/analytics.service';
import { PushNotificationService } from './services/push-notification.service';
import { PlatformService } from './services/platform.service';
import { AppleHealthOutboundSyncService } from './services/apple-health-outbound-sync.service';
import { HealthInboundSyncService } from './services/health-inbound-sync.service';
import { take } from 'rxjs';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule]
})
export class AppComponent implements OnInit {
  title = 'Nutrition Ambition';

  private authService = inject(AuthService);
  private analyticsService = inject(AnalyticsService);
  private pushNotificationService = inject(PushNotificationService);
  private platformService = inject(PlatformService);
  private appleHealthOutboundSync = inject(AppleHealthOutboundSyncService);
  private healthInboundSync = inject(HealthInboundSyncService);
  private hasRegisteredHealthResumeListener = false;

  constructor(
    private accountsService: AccountsService
  ) {}

  async ngOnInit() {
    // 🔹 Auth / Accounts init
    this.authService.authReady$.pipe(take(1)).subscribe({
      next: async () => {
        try {
          const isAuthenticated = await this.authService.isAuthenticated();
          if (isAuthenticated) {
            await this.accountsService.loadAccount();
          }
        } catch (e) {
          // Auth/account initialization failures are non-fatal at startup
        }

        // 🔹 Initialize push notifications on native platforms
        // This checks existing permission state and registers token if already granted
        // It does NOT trigger the OS permission dialog
        if (this.platformService.isNative()) {
          this.pushNotificationService.initializePush().catch(err =>
            console.error('Push initialization failed:', err)
          );
          void this.appleHealthOutboundSync.syncIfConfigured(Capacitor.getPlatform() === 'android' ? 'HealthConnect' : 'AppleHealth').catch(() => {
            // Health sync is best-effort and must never interrupt app startup.
          });
          void this.healthInboundSync.syncTodayIfConfigured(Capacitor.getPlatform() === 'android' ? 'HealthConnect' : 'AppleHealth');
          void this.healthInboundSync.syncHistoryIfConfigured(Capacitor.getPlatform() === 'android' ? 'HealthConnect' : 'AppleHealth');
          this.registerHealthForegroundSync();
        }
      }
    });
  }

  /**
   * A foreground opportunity is a best-effort retry, not a cadence promise.
   * The queue itself checks completed setup and OS sharing before any write.
   */
  private registerHealthForegroundSync(): void {
    if (this.hasRegisteredHealthResumeListener) return;
    this.hasRegisteredHealthResumeListener = true;
    void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) return;
      void this.appleHealthOutboundSync.syncIfConfigured(Capacitor.getPlatform() === 'android' ? 'HealthConnect' : 'AppleHealth').catch(() => {
        // Keep foreground lifecycle work isolated from normal app behavior.
      });
      void this.healthInboundSync.syncTodayIfConfigured(Capacitor.getPlatform() === 'android' ? 'HealthConnect' : 'AppleHealth');
      void this.healthInboundSync.syncHistoryIfConfigured(Capacitor.getPlatform() === 'android' ? 'HealthConnect' : 'AppleHealth');
    });
  }
}
